// DRAFT — REVIEW ARTIFACT — NOT DEPLOYED
//
// The rendered retail newsletter, shown to the human who is about to attest to it, plus the
// integrity strip that says whether the thing on screen is the thing the review row will name.
//
// SANDBOX: srcdoc with sandbox="" — NO allow-scripts, NO allow-same-origin. The frozen HTML is
// our own render, but it is still untrusted input to this page (it is assembled from IR prose
// slots), and nothing in an email needs script or same-origin access to be read.
//
// TWO HONEST CAVEATS, stated ON the surface rather than in a comment nobody reads:
//   * remote images referenced by the email WILL load from their hosts inside the frame;
//   * an iframe is not an email client. The review binds the BYTES, not any client's rendering
//     of them. Outlook/Gmail will lay this out differently and that is not what is being signed.
//
// The integrity strip is the ONE client-side check in this island that HARD-BLOCKS with no
// override. GateTab's artifact mirror soft-blocks because it re-derives a server rule and can
// drift stricter (GateTab.tsx:164-176). This does not re-derive anything: it hashes the bytes
// the browser actually received and compares them to the hash the frozen row names. If those
// differ, the reviewer is not looking at the copy that would be sent, and no attestation about
// it can be truthful.

import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, ShieldX, ShieldAlert, EyeOff } from "lucide-react";
import type { NewsletterPreview } from "@/hooks/investment-reporting/useNewsletterReview";
import { shortHash } from "./labels";

export function NewsletterPreviewFrame({
  preview,
  isLoading,
  className,
}: {
  preview: NewsletterPreview | undefined;
  isLoading: boolean;
  className?: string;
}) {
  if (isLoading) {
    return (
      <Card className={cn("p-4 space-y-3", className)} aria-busy="true" aria-label="Loading the rendered newsletter">
        <Skeleton className="h-4 w-56" />
        <Skeleton className="h-[420px] w-full" />
      </Card>
    );
  }

  if (!preview) return null;

  // --- cannot display: say exactly why, never an empty frame -----------------
  if (preview.unavailable || !preview.html) {
    return (
      <Card className={cn("p-6 space-y-2", className)}>
        <div className="flex items-start gap-2">
          <EyeOff className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" aria-hidden />
          <div className="space-y-1">
            <div className="font-heading text-sm font-medium text-foreground">
              The rendered copy cannot be displayed
            </div>
            <p className="font-body text-xs text-muted-foreground">
              {preview.detail ?? "The frozen newsletter bytes could not be retrieved."}
            </p>
            <p className="font-body text-xs text-muted-foreground">
              Recording a content review is blocked while this is true. A review states that a
              named human read the exact rendered copy — it cannot be recorded against bytes
              nobody in this session can see.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  const ok = preview.integrityOk;

  return (
    <div className={cn("space-y-2", className)}>
      {/* Integrity strip — hard block, not a mirror */}
      <Card className="p-3">
        <div className="flex items-start gap-2">
          {ok ? (
            <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden />
          ) : (
            <ShieldX className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
          )}
          <div className="min-w-0 space-y-0.5">
            <div className="font-body text-xs font-medium text-foreground">
              {ok
                ? "These are the frozen bytes — sha256 of what this browser fetched matches the artifact the review will name."
                : "HASH MISMATCH — what is on screen is NOT the artifact the frozen row names. The review is blocked."}
            </div>
            <div className="font-body text-[11px] text-muted-foreground break-all">
              computed {shortHash(preview.computedSha256, 16)} · named {shortHash(preview.expectedSha256, 16)}
            </div>
            {!ok && preview.detail && (
              <div className="font-body text-[11px] text-destructive">{preview.detail}</div>
            )}
          </div>
        </div>
      </Card>

      {/* The email itself */}
      <Card className="p-0 overflow-hidden">
        <iframe
          title="Rendered retail newsletter (sandboxed preview)"
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={preview.html}
          className="w-full h-[640px] bg-white"
        />
      </Card>

      <div className="flex items-start gap-1.5 font-body text-[11px] text-muted-foreground">
        <ShieldAlert className="h-3 w-3 mt-0.5 shrink-0" aria-hidden />
        <span>
          Sandboxed preview: scripts and same-origin access are disabled, but any remote images the
          email references will load from their own hosts. An iframe is not an email client — the
          review binds the bytes above, not any particular client's rendering of them.
        </span>
      </div>
    </div>
  );
}
